import React, { createContext, useContext, useMemo, useState } from 'react';
import {
  getCurrentLanguage,
  replaceLanguageRoute,
  setCurrentLanguage,
  type UiLanguage,
} from './language';

type LanguageContextValue = {
  language: UiLanguage;
  setLanguage: (language: UiLanguage) => void;
};

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

type LanguageProviderProps = {
  children: React.ReactNode;
  defaultLanguage?: UiLanguage;
  allowLanguageSelection?: boolean;
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  defaultLanguage = 'en',
  allowLanguageSelection = true,
}) => {
  const [language, setLanguageState] = useState<UiLanguage>(() =>
    allowLanguageSelection ? getCurrentLanguage(defaultLanguage) : defaultLanguage
  );

  const setLanguage = (nextLanguage: UiLanguage) => {
    const effectiveLanguage = allowLanguageSelection ? nextLanguage : defaultLanguage;
    setCurrentLanguage(effectiveLanguage);
    if (allowLanguageSelection) {
      replaceLanguageRoute(effectiveLanguage, import.meta.env.BASE_URL);
    }
    setLanguageState(effectiveLanguage);
  };

  const value = useMemo(
    () => ({ language, setLanguage }),
    [language]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }

  return context;
}
